import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChatComponent, CopilotFacadeService } from '@brighthub/copilot';
import { ClientListComponent } from '@brightnav/ui';
import { ActivityInboxService } from '@brighthub/ai-review-ui';
import { FeatureFlagKey, FeatureFlagsService } from '@brighthub/feature-flags';
import { Splitter } from 'primeng/splitter';
import { map } from 'rxjs';
import { BrightnavLayoutComponent } from '../brightnav-layout/brightnav-layout.component';
import { BrightnavLayoutService } from '../brightnav-layout.service';
import { ClientSessionService } from '@brighthub/uisession';
import { TabItem } from '../tab-bar/tab-bar.component';
import { FaClientInfoPanelComponent } from '../fa-client-info-panel/fa-client-info-panel.component';
import type { FaAppTabId } from '../fa-tabs';

export type { FaAppTabId } from '../fa-tabs';

function isFlagOn(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

@Component({
  selector: 'bn-fa-app-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ChatComponent,
    ClientListComponent,
    BrightnavLayoutComponent,
    FaClientInfoPanelComponent,
    Splitter,
  ],
  templateUrl: './fa-app-layout.component.html',
  styleUrl: './fa-app-layout.component.scss',
})
export class FaAppLayoutComponent {
  layoutService = inject(BrightnavLayoutService);
  clientSessionService = inject(ClientSessionService);
  private _copilotFacadeService = inject(CopilotFacadeService);
  private readonly _featureFlags = inject(FeatureFlagsService);
  private readonly _inbox = inject(ActivityInboxService);
  collapsed = computed(() => this.layoutService.collapsed());

  /** Display label for the chat panel header. */
  readonly copilotPanelLabel = signal('Navi');

  /** Active tab in the tab-style menubar; drives which content shows left of chat. */
  readonly activeTab = signal<FaAppTabId>('profile');

  /** When true, chat panel is collapsed to a narrow strip with expand button. */
  readonly copilotCollapsed = signal(false);

  /** When true, main content panel (tab bar + content) is collapsed to a narrow left strip. */
  readonly contentPanelCollapsed = signal(false);

  /** Finances tab visibility — whole tab hidden when flag off. */
  readonly financialSnapshotEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLEFINANCIALSNAPSHOT, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false }
  );

  /** Review tab visibility — whole tab hidden when flag off. */
  readonly aiReviewEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLEAIREVIEW, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false }
  );

  toggleCopilot(): void {
    this.copilotCollapsed.update((v) => !v);
  }

  toggleContentPanel(): void {
    this.contentPanelCollapsed.update((v) => !v);
  }

  /** Active tab item for the collapsed content strip (label + icon). */
  readonly activeTabItem = computed(() => {
    const id = this.activeTab();
    return this.tabItems().find((t) => t.id === id) ?? null;
  });

  onTabSelect(tabId: string): void {
    this.activeTab.set(tabId as FaAppTabId);
  }

  /** Tab IDs that are always pinned in FA layout (cannot be unpinned). */
  readonly faMandatoryTabIds = signal<FaAppTabId[]>(['profile']);

  /** GUID of the client whose page is open, or null before one is fetched.
   *
   * Tracks `selectedSubjectData`, which the session service sets whether the
   * user typed a fresh GUID or picked one of their already-open clients, and
   * clears when the selected client is archived. */
  readonly selectedClientGuid = computed(
    () => this.clientSessionService.selectedSubjectData()?.guid ?? null,
  );

  /** "Taniesha Duncan" rather than a GUID, for the Review tab's header and
   * detail dialog. Null until a client is selected. */
  readonly selectedClientName = computed(() => {
    const subject = this.clientSessionService.selectedSubjectData();
    if (!subject) return null;
    return [subject.firstName, subject.lastName].filter(Boolean).join(' ');
  });

  /** Drives the tab bar's update dot. The bar carries a boolean rather than a
   * count, and rolls it up into the More menu when the tab overflows. */
  readonly reviewNeedsAttention = signal(false);

  /**
   * Tab items for bn-tab-bar (id, label, icon).
   * Finances only when the feature flag is on.
   * Review only when the flag is on and a client is open — there is nothing for it to scope to otherwise.
   */
  readonly tabItems = computed<TabItem[]>(() => {
    const items: TabItem[] = [{ id: 'profile', label: 'Profile', icon: 'pi pi-user' }];
    if (this.financialSnapshotEnabled()) {
      items.push({ id: 'finances', label: 'Finances', icon: 'pi pi-wallet' });
    }
    if (this.aiReviewEnabled() && this.selectedClientGuid()) {
      items.push({
        id: 'review',
        label: 'Review',
        icon: 'pi pi-inbox',
        hasUpdate: this.reviewNeedsAttention(),
      });
    }
    return items;
  });

  constructor() {
    effect(() => {
      const selectedSession = this.clientSessionService.selectedSession();
      if (!selectedSession) {
        this._copilotFacadeService.clearContext();
      }
    });

    effect(() => {
      if (!this.financialSnapshotEnabled() && this.activeTab() === 'finances') {
        this.activeTab.set('profile');
      }
    });

    // Flag off, or archiving the open client, removes the Review tab from the
    // bar. If it was the tab being viewed, fall back to Profile rather than
    // leaving the pane pointed at a tab that no longer exists.
    effect(() => {
      const reviewAvailable =
        this.aiReviewEnabled() && !!this.selectedClientGuid();
      if (!reviewAvailable && this.activeTab() === 'review') {
        this.activeTab.set('profile');
      }
    });

    // Read the client's activities on selection rather than on tab open, so
    // the dot is already right when the tab bar first renders. Skip when the
    // flag is off so we do not hit the inbox API for a hidden tab. A failure
    // just leaves the dot off — the tab itself surfaces the error when opened.
    effect(() => {
      const guid = this.selectedClientGuid();
      this.reviewNeedsAttention.set(false);
      if (!this.aiReviewEnabled() || !guid) return;
      void this._inbox
        .loadClientFeed(guid)
        .then((feed) =>
          this.reviewNeedsAttention.set(
            (feed.countsByStatus['needs_confirmation'] ?? 0) > 0,
          ),
        )
        .catch(() => this.reviewNeedsAttention.set(false));
    });
  }

  ngOnInit() {
    this._copilotFacadeService.hideInput.set(false);
  }
}
